import { Controller } from '@hotwired/stimulus';
import { patch } from '@rails/request.js';
import Sortable from 'sortablejs';

/**
 * Board sortable - drag and drop cards between (and inside) the lists of a board.
 * On drop it PATCHes the card's move endpoint; the turbo-stream response re-renders the affected lists.
 * If the server refuses the move, the page is reloaded so the board shows the real state again.
 * Targets: list - card container, carries data-list-id; each card inside carries data-card-id and data-number
 * Values: url-template (String) - move URL containing `:number`
 * @example
 * <div data-controller="board-sortable" data-board-sortable-url-template-value="/boards/1/cards/:number/move">
 *   <div data-board-sortable-target="list" data-list-id="7">
 *     <article data-card-id="12" data-number="3">…</article>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['list'];
  static values = { urlTemplate: String };

  initialize () {
    this.sortables = new Map();
  }

  disconnect () {
    this.sortables.forEach((sortable) => sortable.destroy());
    this.sortables.clear();
  }

  listTargetConnected (element) {
    this.sortables.get(element)?.destroy();
    this.sortables.set(element, Sortable.create(element, {
      group: 'board-cards',
      draggable: '[data-card-id]',
      animation: 150,
      // Card tiles are links: the native HTML5 drag would drag the link itself, so Sortable drives the drag.
      // The tiles also carry draggable="false", or the browser starts its own link drag before Sortable's
      // (Sortable only clears it on a tile after that tile's first drag).
      forceFallback: true,
      fallbackTolerance: 4,
      // Each list scrolls its own cards: on touch a swipe scrolls it, a long press picks the card up
      delay: 250,
      delayOnTouchOnly: true,
      touchStartThreshold: 5,
      // Scroll the list (or the board) when the card is held near its edge
      scrollSensitivity: 60,
      ghostClass: 'is-ghost',
      chosenClass: 'is-chosen',
      dragClass: 'is-dragging',
      onEnd: (event) => this.move(event)
    }));
  }

  listTargetDisconnected (element) {
    this.sortables.get(element)?.destroy();
    this.sortables.delete(element);
  }

  async move (event) {
    const { item, from, to, oldIndex, newIndex } = event;
    if (from === to && oldIndex === newIndex) return;

    const body = new FormData();
    body.append('board_list_id', to.dataset.listId);
    body.append('before_id', item.nextElementSibling?.dataset.cardId || '');

    const url = this.urlTemplateValue.replace(':number', item.dataset.number);
    const response = await patch(url, { body, responseKind: 'turbo-stream' });
    if (!response.ok) window.Turbo.visit(window.location.href, { action: 'replace' });
  }
}
